import { FC, PropsWithChildren, ReactNode, useEffect } from 'react'
import { Navigate, useParams } from 'react-router'
import { skipToken } from '@reduxjs/toolkit/query'

import { useGetQuoteV2Query, useSendDeviceInfoQuery } from './services'
import { ErrorPage } from '../Error/ErrorPage'
import { InceptionDateBlock } from 'components/InceptionDateBlock'
import Loader from 'components/Loader'

import {
  FeatureFlagsProvider,
  formatDate,
  initAnalytics,
  isKnownQuoteErrorObject,
  Storage as storage,
  useEventTracking,
} from 'utils'
import { createPostQuoteAbTestKeys } from 'utils/abTests/abTestHelpers.helpers'
import { AbTestsProvider } from 'utils/abTests/abTests'
import { directFlowToken } from 'utils/directFlowToken'
import { IntercomPostQuoteContainer } from 'utils/intercomPostQuote'

export const CoreQuoteService: FC<PropsWithChildren> = ({ children }) => {
  const { quoteId } = useParams<{ quoteId: string }>()

  if (!quoteId) {
    throw new Error('Core quote service used without quoteId in route')
  }
  const {
    data: quote,
    isLoading,
    isError,
    error,
    isSuccess: getQuoteQuerySuccess,
    isFetching: isFetchingQuoteQuery,
  } = useGetQuoteV2Query({ quoteId })
  const track = useEventTracking()
  const isPaymentDeclinePage = window.location.pathname.includes('/decline')
  const { isLoading: deviceQueryLoading } = useSendDeviceInfoQuery(
    !getQuoteQuerySuccess || isFetchingQuoteQuery || quote?.payment.paymentReserved
      ? skipToken
      : { quoteId },
  )

  if (isLoading || deviceQueryLoading) {
    return <Loader />
  }

  if ((isError || Boolean(error)) && !isPaymentDeclinePage) {
    if (!isKnownQuoteErrorObject(error)) {
      return <ErrorPage />
    }

    if (error.data.errorCode === 'QUOTE_DECLINED') {
      track('renders as exclusion page')

      // this is needed to avoid the redirect that happens at the beginning
      // of the routing in case the customer has an existing quoteId, see App.tsx
      storage.forget('quoteId')

      return (
        <Navigate
          replace
          to={{
            pathname: `/quote/${quoteId}/exclusion`,
          }}
        />
      )
    } else if (error.data.errorCode === 'START_DATE_IN_PAST') {
      const originalInceptionDate = error.data.payload?.originalInceptionDate as string
      const [year, month, day] = originalInceptionDate.split('-')
      const displayedDate = formatDate(
        new Date(Number(year), Number(month) - 1, Number(day)),
        '1/01/1970',
      )

      const preQuoteKey = quote?.resumeMetadata?.featureFlagUserKey ?? directFlowToken

      return (
        <FeatureFlagsProvider
          preQuoteKey={preQuoteKey}
          postQuoteKey={error.data.payload?.email ?? ''}
        >
          <InceptionDateBlock initialStartDate={displayedDate} />
        </FeatureFlagsProvider>
      )
    }
  }

  return (
    <AbTestsProvider
      preQuoteKeys={{
        userKey: quote?.resumeMetadata?.featureFlagUserKey ?? directFlowToken,
        participantId: quote?.resumeMetadata?.featureFlagUserKey ?? directFlowToken,
      }}
      postQuoteKeys={createPostQuoteAbTestKeys({
        dateOfBirth: quote?.policyHolder.dateOfBirth,
        postalCode: quote?.policyHolder.currentAddress.postalCode,
        firstQuoteId: quote?.relatedQuotes.first,
      })}
    >
      <FeatureFlagsProvider
        preQuoteKey={quote?.resumeMetadata?.featureFlagUserKey ?? directFlowToken}
        postQuoteKey={quote?.policyHolder.email ?? ''}
      >
        <IntercomPostQuoteContainer quoteChannel={quote?.channel.name}>
          <CoreQuoteAnalyticsWrapper hasQuoteData={!!quote}>{children}</CoreQuoteAnalyticsWrapper>
        </IntercomPostQuoteContainer>
      </FeatureFlagsProvider>
    </AbTestsProvider>
  )
}

const CoreQuoteAnalyticsWrapper = ({
  children,
  hasQuoteData,
}: {
  children: ReactNode
  hasQuoteData: boolean
}) => {
  const { quoteId } = useParams<{ quoteId: string }>()

  useEffect(() => {
    if (hasQuoteData && quoteId) {
      initAnalytics(quoteId, undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId])
  return <>{children}</>
}
